// CustomAlert.js - A customizable alert component with window overlay
import React from 'react';
import { Alert, Button } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';

const CustomAlert = ({ 
  show, 
  onClose, 
  variant = 'info', 
  title, 
  message, 
  actionButton = null,
  icon = null,
  dismissible = true,
  autoClose = false,
  autoCloseDelay = 5000,
  size = 'normal', // 'small', 'normal', 'large'
  position = 'center', // 'fixed-top', 'fixed-bottom', 'relative', 'center', 'top-right'
  animation = 'slide', // 'slide', 'fade', 'bounce'
  overlay = true, // Show backdrop overlay
  overlayColor = 'rgba(0, 0, 0, 0.5)' // Backdrop color
}) => {
  
  React.useEffect(() => {
    if (autoClose && show) {
      const timer = setTimeout(() => {
        onClose && onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [show, autoClose, autoCloseDelay, onClose]);

  // Prevent body scroll when alert is shown with overlay
  React.useEffect(() => {
    if (show && overlay) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [show, overlay]);

  const getVariantStyles = () => {
    const styles = {
      success: {
        backgroundColor: '#d1edff',
        borderColor: '#0066cc',
        color: '#003d7a',
        iconColor: '#28a745'
      },
      warning: {
        backgroundColor: '#fff3cd',
        borderColor: '#ffc107',
        color: '#856404',
        iconColor: '#ffc107'
      },
      danger: {
        backgroundColor: '#f8d7da',
        borderColor: '#dc3545',
        color: '#721c24',
        iconColor: '#dc3545'
      },
      info: {
        backgroundColor: '#cce7ff',
        borderColor: '#2482a1',
        color: '#0c5460',
        iconColor: '#2482a1'
      },
      primary: {
        backgroundColor: '#e3f2fd',
        borderColor: '#2482a1',
        color: '#1565c0',
        iconColor: '#2482a1'
      }
    };
    return styles[variant] || styles.info;
  };

  const getSizeStyles = () => {
    const sizes = {
      small: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
      normal: { padding: '1rem 1.25rem', fontSize: '1rem' },
      large: { padding: '1.5rem 2rem', fontSize: '1.125rem' }
    };
    return sizes[size] || sizes.normal;
  };

  const getPositionStyles = () => {
    const positions = {
      'fixed-top': {
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1050,
        width: '90%',
        maxWidth: '600px'
      },
      'fixed-bottom': {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1050,
        width: '400px'
      },
      'center': {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1060,
        width: '90%',
        maxWidth: '500px'
      },
      'top-right': {
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1050,
        width: '400px',
        maxWidth: '90vw'
      },
      relative: {}
    };
    return positions[position] || positions.center;
  };

  const getAnimationVariants = () => {
    const variants = {
      slide: {
        initial: { opacity: 0, y: -50 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -50 }
      },
      fade: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 }
      },
      bounce: {
        initial: { opacity: 0, scale: 0.3 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.3 }
      }
    };
    return variants[animation] || variants.slide;
  };

  const variantStyles = getVariantStyles();
  const sizeStyles = getSizeStyles();
  const positionStyles = getPositionStyles();
  const animationVariants = getAnimationVariants();

  const defaultIcons = {
    success: '✅',
    warning: '⚠️',
    danger: '❌',
    info: 'ℹ️',
    primary: '📌'
  };

  const displayIcon = icon || defaultIcons[variant];

  const needsOverlay = overlay && (position === 'center' || position === 'fixed-top' || position === 'fixed-bottom');

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop Overlay */}
          {needsOverlay && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: overlayColor,
                zIndex: 1055,
                cursor: dismissible ? 'pointer' : 'default'
              }}
              onClick={dismissible ? onClose : undefined}
            />
          )}

          {/* Alert Component */}
          <motion.div
            initial={animationVariants.initial}
            animate={animationVariants.animate}
            exit={animationVariants.exit}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              ...positionStyles,
              zIndex: needsOverlay ? 1060 : positionStyles.zIndex
            }}
          >
            <Alert
              variant={variant}
              dismissible={dismissible}
  onClose={onClose}
              className="border-0 shadow-lg rounded-3"
              style={{
                ...variantStyles,
                ...sizeStyles,
                borderLeft: `5px solid ${variantStyles.borderColor}`,
                fontFamily: "'Inter', sans-serif",
                margin: 0,
                boxShadow: needsOverlay ? '0 10px 30px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.1)'
              }}
            >
              <div className="d-flex align-items-start">
                {displayIcon && (
                  <div 
                    className="me-3 fs-4"
                    style={{ color: variantStyles.iconColor, minWidth: '24px' }}
                  >
                    {displayIcon}
                  </div>
                )}
                
                <div className="flex-grow-1">
                  {title && (
                    <Alert.Heading 
                      className="h5 mb-2 fw-bold"
                      style={{ color: variantStyles.color }}
                    >
                      {title}
                    </Alert.Heading>
                  )}
                  
                  <div 
                    className="mb-0"
                    style={{ 
                      color: variantStyles.color,
                      lineHeight: '1.5'
                    }}
                  >
                    {message}
                  </div>
                  
                  {actionButton && (
                    <div className="mt-3">
                      {actionButton}
                    </div>
                  )}
                </div>
                
                {/* {dismissible && onClose && (
                  <Button
                    variant="link"
                    className="btn-close p-0 ms-2"
                    onClick={onClose}
                    style={{ 
                      color: variantStyles.color,
                      fontSize: '1.2rem',
                      textDecoration: 'none',
                      opacity: 0.7
                    }}
                  >
                    ×
                  </Button>
                )} */}
              </div>
              
              {autoClose && (
                <div 
                  className="mt-2"
                  style={{
                    height: '3px',
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}
                >
                  <motion.div
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: autoCloseDelay / 1000, ease: "linear" }}
                    style={{
                      height: '100%',
                      backgroundColor: variantStyles.borderColor,
                      borderRadius: '2px'
                    }}
                  />
                </div>
              )}
            </Alert>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CustomAlert;